import React from 'react';
import styled, { css, keyframes } from 'styled-components';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'ai';
export type ButtonSize = 'sm' | 'md' | 'lg';

type StyledButtonProps = {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $isLoading: boolean;
};

const sizeStyles: Record<ButtonSize, ReturnType<typeof css>> = {
  sm: css`
    height: 28px;
    padding: 0 var(--space-3);
    font-size: var(--font-size-sm);
    border-radius: var(--radius-sm);
    gap: var(--space-1);
  `,
  md: css`
    height: 36px;
    padding: 0 var(--space-4);
    font-size: var(--font-size-base);
    border-radius: var(--radius-md);
    gap: var(--space-2);
  `,
  lg: css`
    height: 44px;
    padding: 0 var(--space-5);
    font-size: var(--font-size-md);
    border-radius: var(--radius-md);
    gap: var(--space-2);
  `,
};

const variantStyles: Record<ButtonVariant, ReturnType<typeof css>> = {
  primary: css`
    background: var(--color-accent);
    color: var(--color-text-inverse);
    border: 1px solid var(--color-accent);
    &:hover:not(:disabled) {
      background: var(--color-accent-hover);
      border-color: var(--color-accent-hover);
    }
    &:active:not(:disabled) {
      background: var(--color-accent-active);
      border-color: var(--color-accent-active);
    }
  `,
  secondary: css`
    background: var(--color-surface);
    color: var(--color-text);
    border: 1px solid var(--color-border);
    &:hover:not(:disabled) {
      background: var(--color-surface-subtle);
      border-color: var(--color-border-strong);
    }
    &:active:not(:disabled) {
      background: var(--color-surface-inset);
    }
  `,
  ghost: css`
    background: transparent;
    color: var(--color-text-secondary);
    border: 1px solid transparent;
    &:hover:not(:disabled) {
      background: var(--color-surface-subtle);
      color: var(--color-text);
    }
    &:active:not(:disabled) {
      background: var(--color-surface-inset);
    }
  `,
  danger: css`
    background: var(--color-danger);
    color: var(--color-text-inverse);
    border: 1px solid var(--color-danger);
    &:hover:not(:disabled) {
      filter: brightness(1.08);
    }
    &:active:not(:disabled) {
      filter: brightness(0.92);
    }
  `,
  ai: css`
    background: linear-gradient(
      135deg,
      var(--color-accent) 0%,
      var(--color-accent-hover) 100%
    );
    color: var(--color-text-inverse);
    border: 1px solid transparent;
    box-shadow: var(--shadow-sm);
    &:hover:not(:disabled) {
      box-shadow: var(--shadow-md);
      filter: brightness(1.05);
    }
    &:active:not(:disabled) {
      filter: brightness(0.95);
    }
  `,
};

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-sans);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  transition:
    background var(--duration-fast) var(--ease-standard),
    border-color var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard),
    filter var(--duration-fast) var(--ease-standard);
  width: ${(p) => (p.$fullWidth ? '100%' : 'auto')};
  ${(p) => sizeStyles[p.$size]}
  ${(p) => variantStyles[p.$variant]}

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${(p) =>
    p.$isLoading &&
    css`
      color: transparent !important;
      pointer-events: none;
    `}
`;

const Spinner = styled.span`
  position: absolute;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  color: inherit;
`;

const SpinnerWrapper = styled.span`
  position: absolute;
  display: inline-flex;
  color: var(--color-text-inverse);
`;

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  isLoading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      isLoading = false,
      iconLeft,
      iconRight,
      children,
      disabled,
      type = 'button',
      ...rest
    },
    ref
  ) => {
    return (
      <StyledButton
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        $variant={variant}
        $size={size}
        $fullWidth={fullWidth}
        $isLoading={isLoading}
        {...rest}
      >
        {iconLeft && <span aria-hidden="true">{iconLeft}</span>}
        <span>{children}</span>
        {iconRight && <span aria-hidden="true">{iconRight}</span>}
        {isLoading && (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        )}
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';
